/**
 * Direct edits (Rule Book 3.9d) — the pure rules behind the forms that change
 * an operator's data from AffDice SuperAdmin: which status moves to offer, which fields
 * a save sends, which row actions a campaign or a deal offers.
 *
 * Kept out of the .tsx so a test can read it without a DOM. The server
 * applies the same rules again on every request; this only keeps a choice off
 * the screen that would be refused, and keeps a save from sending fields
 * nobody changed (an unchanged contact field sent back would become the
 * operator's own copy of it).
 */

/* ------------------------------------------------------------------ *
 * Affiliate status
 * ------------------------------------------------------------------ */

/**
 * The moves an affiliate's status may make, as the operator's own route
 * allows them (`TRANSITIONS`, apps/api-core/src/admin.ts, Rule Book 6.2).
 * Kept in step by a test that reads both.
 */
export const AFFILIATE_TRANSITIONS: Readonly<Record<string, readonly string[]>> = {
  ACTIVE: ['UNDER_REVIEW', 'SUSPENDED', 'BLOCKED', 'TERMINATED', 'DEACTIVATED'],
  UNDER_REVIEW: ['ACTIVE', 'SUSPENDED', 'BLOCKED', 'TERMINATED'],
  SUSPENDED: ['ACTIVE', 'BLOCKED', 'TERMINATED'],
  BLOCKED: ['ACTIVE', 'TERMINATED'],
  PENDING: ['ACTIVE', 'REJECTED'],
  REJECTED: [],
  TERMINATED: [],
  DEACTIVATED: ['ACTIVE'],
};

/** Where an affiliate in `from` may be moved; empty for a final state. */
export function affiliateStatusTargets(from: string): readonly string[] {
  return AFFILIATE_TRANSITIONS[from] ?? [];
}

/** A move that cannot be undone from here: nothing leaves TERMINATED or REJECTED. */
export function isFinalStatus(to: string): boolean {
  return affiliateStatusTargets(to).length === 0;
}

/* ------------------------------------------------------------------ *
 * Affiliate profile, tags
 * ------------------------------------------------------------------ */

/** The fields the operator keeps on an affiliate (PUT …/profile-fields). */
export const PROFILE_FIELDS = [
  'username',
  'external_affiliate_id',
  'company_name',
  'contact_name',
  'phone',
  'website_urls',
  'marketing_note',
  'country',
] as const;
export type ProfileField = (typeof PROFILE_FIELDS)[number];

export type ProfileDraft = Readonly<Record<ProfileField, string>> & { readonly geos: string };

/** What the form starts from: every field as text, GEOs comma-separated. */
export function profileDraft(a: Partial<Record<ProfileField, string | null>> & { geos?: readonly string[] | null }): ProfileDraft {
  const out = Object.fromEntries(PROFILE_FIELDS.map((f) => [f, a[f] ?? ''])) as Record<ProfileField, string>;
  return { ...out, geos: (a.geos ?? []).join(', ') };
}

/** GEOs typed as "gb, ng ng" → ["GB", "NG"]: split on commas and spaces, upper-cased, once each. */
export function parseGeos(text: string): string[] {
  return [...new Set(text.split(/[\s,]+/).map((g) => g.trim().toUpperCase()).filter((g) => g !== ''))];
}

/**
 * The body for PUT …/profile-fields: only what changed. An emptied field is
 * sent as '' (the route clears it); an untouched one is left out, so the
 * affiliate's own value is not turned into the operator's copy of it.
 * Null when nothing changed.
 */
export function profileChanges(
  original: ProfileDraft,
  draft: ProfileDraft,
): Record<string, string | string[]> | null {
  const out: Record<string, string | string[]> = {};
  for (const f of PROFILE_FIELDS) {
    const next = f === 'country' ? draft[f].trim().toUpperCase() : draft[f].trim();
    if (next !== original[f].trim()) out[f] = next;
  }
  const geos = parseGeos(draft.geos);
  if (geos.join(',') !== parseGeos(original.geos).join(',')) out['geos'] = geos;
  return Object.keys(out).length === 0 ? null : out;
}

/** Tags typed as "vip, sports" → ["vip", "sports"], each once whatever its case. */
export function parseTags(text: string): string[] {
  const seen = new Map<string, string>();
  for (const raw of text.split(',')) {
    const name = raw.trim();
    if (name !== '' && !seen.has(name.toLowerCase())) seen.set(name.toLowerCase(), name);
  }
  return [...seen.values()];
}

/** Whether two tag lists differ, ignoring order and case. */
export function tagsChanged(before: readonly string[], after: readonly string[]): boolean {
  const key = (l: readonly string[]) => l.map((n) => n.toLowerCase()).sort().join('\n');
  return key(before) !== key(after);
}

/* ------------------------------------------------------------------ *
 * Operator company profile
 * ------------------------------------------------------------------ */

export const COMPANY_FIELDS = ['legal_name', 'address_line', 'city', 'region', 'postal_code', 'country'] as const;
export type CompanyField = (typeof COMPANY_FIELDS)[number];
export type CompanyDraft = Readonly<Record<CompanyField, string>>;

export function companyDraft(o: Readonly<Record<string, unknown>>): CompanyDraft {
  return Object.fromEntries(
    COMPANY_FIELDS.map((f) => [f, o[f] === null || o[f] === undefined ? '' : String(o[f])]),
  ) as CompanyDraft;
}

/**
 * The body for PUT /v1/admin/profile/company: only what changed. An emptied
 * optional field is sent as null (cleared); the legal name cannot be emptied
 * — `companyProblem` says so before the server has to.
 */
export function companyChanges(original: CompanyDraft, draft: CompanyDraft): Record<string, string | null> | null {
  const out: Record<string, string | null> = {};
  for (const f of COMPANY_FIELDS) {
    const next = f === 'country' ? draft[f].trim().toUpperCase() : draft[f].trim();
    if (next === original[f].trim()) continue;
    out[f] = next === '' && f !== 'legal_name' ? null : next;
  }
  return Object.keys(out).length === 0 ? null : out;
}

/** What is wrong with the draft before it is sent, or null. */
export function companyProblem(draft: CompanyDraft): 'legal-name' | 'country' | null {
  if (draft.legal_name.trim() === '') return 'legal-name';
  const c = draft.country.trim();
  if (c !== '' && !/^[A-Za-z]{2}$/.test(c)) return 'country';
  return null;
}

/* ------------------------------------------------------------------ *
 * Brand profile
 * ------------------------------------------------------------------ */

export interface BrandProfileDraft {
  readonly display_name: string;
  readonly base_currency: string;
  readonly default_url: string;
}

/**
 * The body for PATCH /v1/admin/brands/:id — only what changed. The web app
 * URL can be changed but not removed (the API refuses it), so an emptied box
 * is no change rather than a request the server will turn down.
 */
export function brandProfileChanges(
  original: BrandProfileDraft,
  draft: BrandProfileDraft,
): Record<string, string | null> | null {
  const out: Record<string, string | null> = {};
  if (draft.display_name.trim() !== original.display_name.trim()) out['display_name'] = draft.display_name.trim();
  if (draft.base_currency !== original.base_currency) out['base_currency'] = draft.base_currency;
  if (draft.default_url.trim() !== '' && draft.default_url.trim() !== original.default_url.trim()) {
    out['default_url'] = draft.default_url.trim();
  }
  return Object.keys(out).length === 0 ? null : out;
}

/**
 * A proposal (BRAND_PROFILE) carries a name and a currency only, so it can
 * stand in for a save only when the default URL is not part of the change.
 */
export function proposableBrandChange(changes: Record<string, string | null> | null): boolean {
  return changes !== null && !('default_url' in changes);
}

/* ------------------------------------------------------------------ *
 * Settings
 * ------------------------------------------------------------------ */

/** A setting's value as typed, read as the kind the setting holds. */
export function settingValueFromText(kind: 'number' | 'string' | 'boolean' | 'object', raw: string): unknown {
  if (kind === 'number') return Number(raw);
  if (kind === 'boolean') return raw === 'true';
  if (kind === 'object') {
    try { return JSON.parse(raw); } catch { return raw; }
  }
  return raw;
}

/** The text a setting's value starts from in its form. */
export function settingValueText(v: unknown): string {
  return v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
}

/** Whether a typed value would be refused for its kind before it is sent. */
export function settingValueProblem(kind: 'number' | 'string' | 'boolean' | 'object', raw: string): boolean {
  if (raw.trim() === '') return true;
  if (kind === 'number') return !Number.isFinite(Number(raw));
  return false;
}

/* ------------------------------------------------------------------ *
 * Campaigns and deals
 * ------------------------------------------------------------------ */

export type CampaignAction = 'pause' | 'resume' | 'retire';

/**
 * What a campaign row offers. A draft has not gone live, so there is nothing
 * to pause; a retired one takes no write at all.
 */
export function campaignActions(state: string | null, edit: boolean): CampaignAction[] {
  if (!edit || state === null || state === 'RETIRED') return [];
  const out: CampaignAction[] = [];
  if (state === 'ACTIVE') out.push('pause');
  if (state === 'PAUSED') out.push('resume');
  out.push('retire');
  return out;
}

export type DealAction = 'end' | 'reactivate';

export interface DealRow {
  readonly live_version: number | null;
  readonly live_valid_to: string | null;
  readonly ended_at: string | null;
  readonly reactivated_as: string | null;
  readonly pending_version: number | null;
}

/** Whether the deal has stopped: every agreed version has an end and it is past. */
export function dealEnded(d: DealRow, now: Date): boolean {
  return d.ended_at !== null && new Date(d.ended_at).getTime() <= now.getTime();
}

/**
 * What a deal row offers. End needs terms that are running with no end set;
 * re-activate needs a deal that has ended, has not been re-activated already
 * and has no offer awaiting an answer.
 */
export function dealActions(d: DealRow, now: Date, edit: boolean): DealAction[] {
  if (!edit) return [];
  if (dealEnded(d, now)) {
    return d.reactivated_as === null && d.pending_version === null ? ['reactivate'] : [];
  }
  if (d.live_version !== null && d.live_valid_to === null && d.ended_at === null) return ['end'];
  return [];
}
